import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { FileText, Send, Loader2, Bug } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Change {
  id: string;
  type: "addition" | "deletion" | "replace";
  text: string;
  originalText?: string;
  position: number;
  context_before?: string;
  context_after?: string;
}

interface Comment {
  id: string;
  text: string;
  textSelections: any[];
  isGeneral: boolean;
  context_before?: string;
  context_after?: string;
}

interface ChangeSummaryProps {
  messageId: string;
  originalContent: string;
  latestContent: string;
  changes: Change[];
  comments: Comment[];
  onSend: (summary: string) => void;
}

export const ChangeSummary = ({
  messageId,
  originalContent,
  latestContent,
  changes,
  comments,
  onSend,
}: ChangeSummaryProps) => {
  const [summary, setSummary] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [debugDump, setDebugDump] = useState("");
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Auto-save summary with debounce - max 15 seconds of unsaved work
  useEffect(() => {
    if (summary.trim()) {
      // Clear existing timer
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      
      // Set new timer for 15 seconds
      autoSaveTimerRef.current = setTimeout(async () => {
        try {
          await supabase
            .from('messages')
            .update({ custom_summary: summary })
            .eq('id', messageId);
        } catch (error) {
          console.error('Error auto-saving summary:', error);
        }
      }, 15000);
    }

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [summary, messageId]);

  const handleGenerateSummary = async () => {
    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-change-summary', {
        body: { originalContent, latestContent, changes, comments }
      });

      if (error) throw error;

      setSummary(data.summary);
      toast.success('Summary generated successfully');
    } catch (error: any) {
      console.error('Error generating summary:', error);
      toast.error(error.message || 'Failed to generate summary');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDebugDump = () => {
    const dump = {
      messageId,
      timestamp: new Date().toISOString(),
      originalContent,
      latestContent,
      changes: changes.map(c => ({
        id: c.id,
        type: c.type,
        text: c.text,
        originalText: c.originalText,
        position: c.position,
        context_before: c.context_before,
        context_after: c.context_after,
      })),
      comments: comments.map(c => ({
        id: c.id,
        text: c.text,
        isGeneral: c.isGeneral,
        textSelections: c.textSelections,
        context_before: c.context_before,
        context_after: c.context_after,
      })),
      metadata: {
        totalChanges: changes.length,
        totalComments: comments.length,
        linkedComments: comments.filter(c => !c.isGeneral).length,
        generalComments: comments.filter(c => c.isGeneral).length,
        replaceChanges: changes.filter(c => c.type === 'replace').length,
        additions: changes.filter(c => c.type === 'addition').length,
        deletions: changes.filter(c => c.type === 'deletion').length,
      },
    };
    
    const dumpText = JSON.stringify(dump, null, 2);
    setDebugDump(dumpText);
    
    // Copy to clipboard
    navigator.clipboard.writeText(dumpText).then(() => {
      toast.success('Debug data copied to clipboard');
    });
  };

  const handleSend = () => {
    if (summary.trim()) {
      onSend(summary);
    }
  };

  return (
    <Card className="p-4 border-t">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm flex items-center gap-2">
            <FileText className="w-4 h-4" />
            My response
          </h3>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleDebugDump}
            >
              <Bug className="w-4 h-4 mr-1" />
              Debug Dump
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleGenerateSummary}
              disabled={isGenerating}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-1" />
                  Draft my response
                </>
              )}
            </Button>
          </div>
        </div>

        <Textarea
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          placeholder="Click 'Draft my response' to generate AI draft, or write your own..."
          className="min-h-[100px] resize-none"
        />

        {debugDump && (
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-muted-foreground">Debug Data (copied to clipboard):</h4>
            <pre className="bg-muted p-4 rounded-md overflow-auto max-h-96">
              <code className="font-mono text-xs">{debugDump}</code>
            </pre>
          </div>
        )}

        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={handleSend}
            disabled={!summary.trim()}
          >
            <Send className="w-4 h-4 mr-1" />
            Send
          </Button>
        </div>
      </div>
    </Card>
  );
};
